// Base libs
import { GuxFormFieldRadio } from "genesys-spark-components-react";
import { EvaluationQuestionAutomatedScoringFocusEnum } from "@genesys/cloud-frontend/models";

// General commons
import { useStrings } from "features/i18n/useStrings";

// Locales
import strings from "./EvaluationFocusSection.strings";

// Styles
// Intentionally reusing AutomatedScoringSection styles for visual consistency
import "./AutomatedScoringSection.less";

function EvaluationFocusSectionOption({
    questionId,
    option,
    selected,
    onSelected,
}: {
    questionId: string;
    option: EvaluationQuestionAutomatedScoringFocusEnum;
    selected: boolean;
    onSelected: (option: EvaluationQuestionAutomatedScoringFocusEnum) => void;
}) {
    const t = useStrings(strings);

    return (
        <span>
            <GuxFormFieldRadio
                className={`EvaluationFocusSectionOption ${
                    selected ? "selected" : ""
                }`}
            >
                <input
                    slot="input"
                    type="radio"
                    name={`${questionId}-evaluationFocus`}
                    value={option}
                    checked={selected}
                    onChange={({ target }) => {
                        onSelected(
                            target.value as EvaluationQuestionAutomatedScoringFocusEnum
                        );
                    }}
                    data-testid={`evaluation-focus-option-${option}`}
                />
                <label slot="label">
                    <span>{t.labels.options[option]}</span>
                </label>
            </GuxFormFieldRadio>
        </span>
    );
}

export default function EvaluationFocusSection({
    questionId,
    automatedScoringFocus,
    onChange,
}: {
    questionId: string;
    automatedScoringFocus:
        | EvaluationQuestionAutomatedScoringFocusEnum
        | undefined;
    onChange: (focus: EvaluationQuestionAutomatedScoringFocusEnum) => void;
}) {
    const t = useStrings(strings);
    // Treat undefined as Full Interaction (backend default)
    const effectiveFocus =
        automatedScoringFocus ??
        EvaluationQuestionAutomatedScoringFocusEnum.FullInteraction;

    return (
        <div className="EvaluationFocusSection" data-testid="evaluation-focus">
            <label className="EvaluationFocusSectionTitle">
                {t.labels.title}
            </label>
            <div
                className="EvaluationFocusSectionOptions"
                data-testid="evaluation-focus-options"
            >
                {[
                    EvaluationQuestionAutomatedScoringFocusEnum.FullInteraction,
                    EvaluationQuestionAutomatedScoringFocusEnum.EvaluatedAgent,
                ].map((focus) => (
                    <EvaluationFocusSectionOption
                        key={`${questionId}-${focus}`}
                        questionId={questionId}
                        option={focus}
                        selected={effectiveFocus === focus}
                        onSelected={onChange}
                    />
                ))}
            </div>
        </div>
    );
}
